/**
 * Human names for the numeric enums a create_shipment call carries. The confirmation
 * card is what the person checks before money is committed — "Vervoerder 1, Bezorgtype
 * 2" makes them approve codes they cannot verify, which defeats the card's purpose.
 *
 * Ids follow the MyParcel API. Every lookup returns a fallback that keeps the raw
 * value visible, so an id that is not in these maps degrades to what the card showed
 * before, never to a wrong name.
 */

export type CarrierDisplay = {
  name: string;
  /** Slug in MyParcel's own logo bucket; every one of these was checked to resolve. */
  logoSlug: string;
};

const CARRIERS_BY_ID: Record<number, CarrierDisplay> = {
  1: { name: 'PostNL', logoSlug: 'postnl' },
  2: { name: 'Bpost', logoSlug: 'bpost' },
  4: { name: 'DPD', logoSlug: 'dpd' },
  9: { name: 'DHL For You', logoSlug: 'dhlforyou' },
  10: { name: 'DHL Parcel Connect', logoSlug: 'dhlparcelconnect' },
  11: { name: 'DHL Europlus', logoSlug: 'dhleuroplus' },
  12: { name: 'UPS Standard', logoSlug: 'ups' },
  13: { name: 'UPS Express Saver', logoSlug: 'ups' },
  14: { name: 'GLS', logoSlug: 'gls' },
  16: { name: 'Trunkrs', logoSlug: 'trunkrs' },
};

const PACKAGE_TYPES_NL: Record<number, string> = {
  1: 'Pakket',
  2: 'Brievenbuspakje',
  3: 'Brief',
  4: 'Digitale postzegel',
  6: 'Klein pakket',
};

const DELIVERY_TYPES_NL: Record<number, string> = {
  1: 'Ochtendlevering',
  2: 'Standaard',
  3: 'Avondlevering',
  4: 'Ophalen bij ophaalpunt',
  5: 'Zelfde dag',
  6: 'Maandaglevering',
};

/** The boolean extras, in the order they should read on the card. */
const OPTION_FLAGS_NL: Array<[key: string, label: string]> = [
  ['signature', 'Handtekening voor ontvangst'],
  ['only_recipient', 'Alleen ontvanger'],
  ['return', 'Retour bij geen gehoor'],
  ['large_format', 'Groot formaat'],
  ['age_check', 'Leeftijdscontrole 18+'],
];

export function carrierDisplay(id: unknown): CarrierDisplay | null {
  return typeof id === 'number' ? (CARRIERS_BY_ID[id] ?? null) : null;
}

export function carrierLogoUrl(carrier: CarrierDisplay): string {
  return `https://assets.myparcel.nl/skin/general-images/carrier-logos/svg/${carrier.logoSlug}.svg`;
}

export function packageTypeName(id: unknown): string {
  return typeof id === 'number' ? (PACKAGE_TYPES_NL[id] ?? `Type ${id}`) : String(id);
}

export function deliveryTypeName(id: unknown): string {
  return typeof id === 'number' ? (DELIVERY_TYPES_NL[id] ?? `Type ${id}`) : String(id);
}

/** Names of every option flag that is enabled — boolean true or, on the wire, 1. */
export function enabledOptionNames(options: Record<string, unknown>): string[] {
  return OPTION_FLAGS_NL.filter(([key]) => options[key] === true || options[key] === 1).map(
    ([, label]) => label,
  );
}
